import { FormErrors } from '@/components/ui/FormErrors';
import { applyServerErrors } from '@/lib/validation/server-errors';
import { AccountAppearance } from '@/features/avatar/AccountAppearance';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import {
  AlertCircleIcon,
  CheckIcon,
  LockIcon,
  MailIcon,
  PhoneIcon,
  SpinnerIcon,
  UsersIcon,
} from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { PasswordField } from '@/features/auth/components/PasswordField';
import { useAuth } from '@/features/auth/context/auth-context';
import { authToken } from '@/lib/api';
import {
  accountApi,
  type AccountFormValues,
  type AccountProfile,
  type PasswordValues,
} from './account.api';

// Une sauvegarde terminée après déconnexion ne doit pas remplacer la nouvelle session.
const useActiveForm = () => {
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  return active;
};

const roleLabels = {
  ADMIN: 'Administrateur',
  RECRUITER: 'Recruteur',
  HIRING_MANAGER: 'Manager',
  VIEWER: 'Lecteur',
  CANDIDATE: 'Consultant',
};
const messageOf = (error: unknown) =>
  error instanceof Error
    ? error.message
    : 'Impossible d’enregistrer. Réessayez dans quelques instants.';

const Notice = ({ children, error = false }: { children: ReactNode; error?: boolean }) => (
  <div
    role={error ? 'alert' : 'status'}
    className={`flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm leading-6 ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}
  >
    <span aria-hidden className="mt-1 shrink-0">
      {error ? <AlertCircleIcon /> : <CheckIcon />}
    </span>
    {children}
  </div>
);

const AccountSection = ({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description: string;
  icon: ReactNode;
  children: ReactNode;
}) => (
  <section
    aria-label={title}
    className="border-navy-200/70 min-w-0 overflow-hidden rounded-[10px] border bg-white shadow-[inset_0_1px_0_0_white,0_4px_16px_-8px_rgb(15_42_95/0.1)]"
  >
    <header className="border-navy-100 bg-navy-50/40 flex items-start gap-3 border-b bg-linear-to-b from-white/90 to-transparent p-5">
      <span
        aria-hidden
        className="border-navy-200/60 text-navy-500 grid size-9 shrink-0 place-items-center rounded-lg border bg-white text-lg shadow-[inset_0_1px_0_0_white]"
      >
        {icon}
      </span>
      <div>
        <h2 className="text-navy-900 text-base font-semibold">{title}</h2>
        <p className="text-ink-500 mt-1 text-sm leading-6">{description}</p>
      </div>
    </header>
    {children}
  </section>
);

const PersonalInformation = ({
  profile,
  onSaved,
}: {
  profile: AccountProfile;
  onSaved: (profile: AccountProfile) => void;
}) => {
  const active = useActiveForm();
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);
  const {
    register,
    handleSubmit,
    setError: setFieldError,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<AccountFormValues>({
    defaultValues: {
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: profile.phone ?? '',
    },
  });
  const submit = handleSubmit(async (values) => {
    setError(null);
    setSaved(false);
    try {
      const updated = await accountApi.update(values);
      if (!active.current) return;
      reset({
        firstName: updated.firstName,
        lastName: updated.lastName,
        phone: updated.phone ?? '',
      });
      onSaved(updated);
      setSaved(true);
    } catch (cause) {
      applyServerErrors(cause, setFieldError);
      setError(cause instanceof Error ? cause : new Error(messageOf(cause)));
    }
  });
  return (
    <AccountSection
      title="Informations personnelles"
      description={
        accountApi.isConsultant
          ? 'Votre identité et votre adresse de connexion sont gérées par votre interlocuteur ubeefy.'
          : 'Ces informations permettent à vos collègues de vous identifier.'
      }
      icon={<UsersIcon />}
    >
      <form
        onChange={() => setError(null)}
        onSubmit={(event) => {
          void submit(event);
        }}
        className="space-y-5 p-5 sm:p-6"
      >
        <fieldset disabled={isSubmitting} className="grid min-w-0 gap-5 sm:grid-cols-2">
          <TextField
            label="Prénom"
            autoComplete="given-name"
            readOnly={accountApi.isConsultant}
            maxLength={100}
            error={errors.firstName?.message}
            {...register('firstName', {
              validate: (value) =>
                accountApi.isConsultant || !!value.trim() || 'Le prénom est requis.',
            })}
          />
          <TextField
            label="Nom"
            autoComplete="family-name"
            readOnly={accountApi.isConsultant}
            maxLength={100}
            error={errors.lastName?.message}
            {...register('lastName', {
              validate: (value) =>
                accountApi.isConsultant || !!value.trim() || 'Le nom est requis.',
            })}
          />
          <div className="sm:col-span-2">
            <TextField
              label="Adresse de connexion"
              type="email"
              icon={<MailIcon />}
              value={profile.email}
              readOnly
              hint="Pour changer cette adresse, contactez votre administrateur."
            />
          </div>
          {accountApi.isConsultant && (
            <div className="sm:col-span-2">
              <TextField
                label="Téléphone"
                type="tel"
                autoComplete="tel"
                icon={<PhoneIcon />}
                maxLength={40}
                hint="Ce numéro sera également mis à jour sur votre fiche consultant."
                error={errors.phone?.message}
                {...register('phone', {
                  validate: (value) =>
                    !value.trim() ||
                    (/^\+?[\d\s().-]+$/.test(value.trim()) &&
                      value.replace(/\D/g, '').length >= 7) ||
                    'Indiquez un numéro de téléphone valide.',
                })}
              />
            </div>
          )}
        </fieldset>
        <FormErrors errors={errors} error={error} />
        {saved && !isDirty && <Notice>Vos informations ont été enregistrées.</Notice>}
        <div className="border-navy-100 flex flex-wrap justify-end gap-2 border-t pt-5">
          {isDirty && (
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => {
                reset();
                setError(null);
                setSaved(false);
              }}
            >
              Annuler
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            loadingLabel="Enregistrement…"
            disabled={!isDirty}
          >
            Enregistrer les informations
          </Button>
        </div>
      </form>
    </AccountSection>
  );
};

const PasswordSettings = () => {
  const active = useActiveForm();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<Error | null>(null);
  const {
    register,
    handleSubmit,
    setError: setFieldError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues>({
    defaultValues: { currentPassword: '', newPassword: '', confirmation: '' },
  });
  const submit = handleSubmit(async (values) => {
    setError(null);
    try {
      await accountApi.changePassword(values);
      if (!active.current) return;
      await logout();
      await navigate(ROUTES.login, { replace: true, state: { passwordChanged: true } });
    } catch (cause) {
      applyServerErrors(cause, setFieldError);
      setError(cause instanceof Error ? cause : new Error(messageOf(cause)));
    }
  });
  return (
    <AccountSection
      title="Mot de passe"
      description="Choisissez un mot de passe réservé à votre compte ubeefy."
      icon={<LockIcon />}
    >
      <form
        onChange={() => setError(null)}
        onSubmit={(event) => {
          void submit(event);
        }}
        className="space-y-5 p-5 sm:p-6"
      >
        <fieldset disabled={isSubmitting} className="grid min-w-0 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <PasswordField
              label="Mot de passe actuel"
              autoComplete="current-password"
              maxLength={128}
              error={errors.currentPassword?.message}
              {...register('currentPassword', { required: 'Saisissez votre mot de passe actuel.' })}
            />
          </div>
          <PasswordField
            label="Nouveau mot de passe"
            autoComplete="new-password"
            maxLength={128}
            hint="12 caractères minimum, avec une majuscule, une minuscule et un chiffre."
            error={errors.newPassword?.message}
            {...register('newPassword', {
              required: 'Saisissez un nouveau mot de passe.',
              minLength: { value: 12, message: 'Utilisez au moins 12 caractères.' },
              validate: (value) =>
                !/[a-z]/.test(value) || !/[A-Z]/.test(value) || !/\d/.test(value)
                  ? 'Ajoutez une majuscule, une minuscule et un chiffre.'
                  : value !== getValues('currentPassword') ||
                    'Choisissez un mot de passe différent.',
            })}
          />
          <PasswordField
            label="Confirmer le nouveau mot de passe"
            autoComplete="new-password"
            maxLength={128}
            error={errors.confirmation?.message}
            {...register('confirmation', {
              required: 'Confirmez votre nouveau mot de passe.',
              validate: (value) =>
                value === getValues('newPassword') || 'Les mots de passe ne correspondent pas.',
            })}
          />
        </fieldset>
        <FormErrors errors={errors} error={error} />
        <div className="border-navy-100 flex flex-wrap items-center justify-between gap-4 border-t pt-5">
          <p className="text-ink-500 max-w-sm text-xs leading-5">
            Après la modification, vos sessions seront fermées. Vous pourrez vous reconnecter avec
            votre nouveau mot de passe.
          </p>
          <Button type="submit" loading={isSubmitting} loadingLabel="Modification…">
            Modifier le mot de passe
          </Button>
        </div>
      </form>
    </AccountSection>
  );
};

export const AccountPage = () => {
  const { user, setSession } = useAuth();
  const cache = useQueryClient();
  const queryKey = ['account', user?.id];
  const profile = useQuery({
    queryKey,
    queryFn: accountApi.read,
    enabled: !!user,
    refetchOnWindowFocus: false,
  });
  const saved = (updated: AccountProfile) => {
    cache.setQueryData(queryKey, updated);
    const token = authToken.get();
    if (token) setSession(token, updated);
    if (accountApi.isConsultant) void cache.invalidateQueries({ queryKey: ['portal'] });
  };
  const identity = profile.data ?? user;
  const initials =
    `${identity?.firstName.trim().at(0) ?? ''}${identity?.lastName.trim().at(0) ?? ''}`.toUpperCase();
  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <span
          aria-hidden
          className="border-navy-200/70 text-navy-600 grid size-11 place-items-center rounded-[10px] border bg-white text-xl shadow-sm"
        >
          <UsersIcon />
        </span>
        <div>
          <h1 className="text-navy-900 text-2xl font-semibold tracking-tight">Mon compte</h1>
          <p className="text-ink-500 mt-1 text-sm">
            Vos informations personnelles et la sécurité de votre accès.
          </p>
        </div>
      </header>
      <div className="grid items-start gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside
          aria-label="Votre compte"
          className="border-navy-200/70 overflow-hidden rounded-[10px] border bg-white shadow-[0_4px_16px_-8px_rgb(15_42_95/0.1)]"
        >
          <div className="bg-navy-50/70 bg-linear-to-b from-white p-6">
            <span
              aria-hidden
              className="bg-navy-900 mb-4 grid size-16 place-items-center rounded-2xl bg-linear-to-b from-navy-700 to-navy-900 text-xl font-semibold text-white shadow-[inset_0_1px_0_0_rgb(255_255_255/0.25)]"
            >
              {identity?.avatarUrl ? (
                <img
                  src={identity.avatarUrl}
                  alt=""
                  className="size-full rounded-2xl object-cover"
                />
              ) : (
                initials || <UsersIcon />
              )}
            </span>
            <h2 className="text-navy-900 text-lg font-semibold wrap-anywhere">
              {[identity?.firstName, identity?.lastName].filter(Boolean).join(' ') || 'Mon compte'}
            </h2>
            <p className="text-ink-500 mt-1 text-sm wrap-anywhere">{identity?.email}</p>
            <span className="border-accent-200/60 bg-accent-50 text-accent-800 mt-4 inline-flex rounded-md border px-2.5 py-1 text-xs font-medium">
              {identity ? roleLabels[identity.role] : 'Compte'}
            </span>
          </div>
          <div className="border-navy-100 border-t px-6 py-4">
            <p className="text-navy-800 text-sm font-medium">
              {accountApi.isConsultant ? 'Espace consultant' : 'Espace interne'}
            </p>
            <p className="text-ink-500 mt-1 text-xs leading-5">
              {accountApi.isConsultant
                ? 'Pour une correction de votre identité ou de votre profil professionnel, contactez votre interlocuteur ubeefy.'
                : 'Votre rôle et vos permissions sont gérés par votre administrateur.'}
            </p>
          </div>
          {profile.data && (
            <AccountAppearance key={profile.data.id} profile={profile.data} onSaved={saved} />
          )}
        </aside>
        <div className="min-w-0 space-y-6">
          {profile.isPending ? (
            <p role="status" className="text-ink-500 flex items-center gap-2 p-6 text-sm">
              <SpinnerIcon />
              Chargement de votre compte…
            </p>
          ) : profile.isError ? (
            <div className="space-y-3">
              <Notice error>{messageOf(profile.error)}</Notice>
              <Button
                variant="secondary"
                onClick={() => {
                  void profile.refetch();
                }}
              >
                Réessayer
              </Button>
            </div>
          ) : (
            <>
              <PersonalInformation key={profile.data.id} profile={profile.data} onSaved={saved} />
              <PasswordSettings />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
